import { ActionBar, CommonPage, Content, InRow, LoadableContent, OpenablePanel, PageTitle, ResponsiveGap } from "common/Grid";

import { TableView } from "./TableView";
import {
  useGetSalesLeadsListQuery,
  useImportSalesLeadMutation,
} from "store/action/salesLeadsApi";
import { useGetSalesUsersListQuery } from "store/action/salesUserApi";
import { PrimaryButton, SecondaryButton } from "common/Forms";
import { useNavigate, createSearchParams, useSearchParams } from "react-router-dom";
import { WithPagination } from "common/Pagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PermissionKeys } from "Constant/Constant";
import { IfHasPermission } from "context/PermissionsContext";
import { convertDateToJson, convertJsonToDate } from "Constant/utils";
import { GrClose } from "react-icons/gr";
import { toast } from "react-toastify";
import { captureComponentError } from "utils/sentry";
import { AdvancedSearchForm } from "./AdvancedSearchForm";
import ImportSalesLead from "./ImportSalesLead";

export const Page = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isImportDialogOpened, setIsImportDialogOpened] = useState(false);
  const [importSalesLead, { isLoading: isImporting }] = useImportSalesLeadMutation();

  const handleImportSalesLead = async (file) => {
    try {
      const response = await importSalesLead({ sheetBase64: file }).unwrap();
      toast.success(response?.message || "Sales leads imported successfully");
      setIsImportDialogOpened(false);
      return true;
    } catch (error) {
      captureComponentError(error, { component: "SalesLeadsList", action: "handleImportSalesLead" });
      toast.error(error?.data?.message || "Something went wrong while importing!");
      return false;
    }
  };
  const searchFields = useMemo(
    () => ({
      page: { name: "p", default: 1 },
      limit: { name: "ps", default: 10 },
      status: { name: "status", default: "" },
      search: { name: "search", default: "" },
      startDate: { name: "startDate", default: "" },
      endDate: { name: "endDate", default: "" },
      assignedTo: { name: "assignedTo", default: "" },
    }),
    [],
  );

  const getSearchValue = useCallback(
    (name, defaultValue, getter = (value) => value) => {
      const value = searchParams.get(name);
      if (value === null) return defaultValue;
      return getter(value);
    },
    [searchParams],
  );

  const page = useRef(
    getSearchValue(
      searchFields.page.name,
      searchFields.page.default,
      (x) => parseInt(x) ?? searchFields.page.default,
    ),
  );
  const limit = useRef(
    getSearchValue(
      searchFields.limit.name,
      searchFields.limit.default,
      (x) => parseInt(x) ?? searchFields.limit.default,
    ),
  );

  const [sorting, setSorting] = useState({ sortBy: null, sortOrder: null });

  const onSortChange = useCallback((sortBy, sortOrder) => {
    setSorting(prev =>
      prev.sortBy === sortBy && prev.sortOrder === sortOrder
        ? { sortBy: null, sortOrder: null }
        : { sortBy, sortOrder }
    );
  }, []);

  const [filters, setFilters] = useState({
    status: getSearchValue(searchFields.status.name, [], v => v ? v.split(',') : []),
    search: getSearchValue(searchFields.search.name, ""),
    startDate: getSearchValue(searchFields.startDate.name, undefined, (v) => convertJsonToDate(v)),
    endDate: getSearchValue(searchFields.endDate.name, undefined, (v) => convertJsonToDate(v)),
    assignedTo: getSearchValue(searchFields.assignedTo.name, ""),
  });

  const applySearch = useCallback(
    (overrideFilters) => {
      const activeFilters = overrideFilters ?? filters;
      const updatedSearchParams = createSearchParams(searchParams);
      updatedSearchParams.set(searchFields.page.name, `${page.current}`);
      updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);
      if (activeFilters.status?.length > 0)
        updatedSearchParams.set(searchFields.status.name, activeFilters.status.join(','));
      else updatedSearchParams.delete(searchFields.status.name);
      if (activeFilters.search)
        updatedSearchParams.set(searchFields.search.name, activeFilters.search);
      else updatedSearchParams.delete(searchFields.search.name);
      if (activeFilters.startDate)
        updatedSearchParams.set(searchFields.startDate.name, convertDateToJson(activeFilters.startDate, true) ?? "");
      else updatedSearchParams.delete(searchFields.startDate.name);
      if (activeFilters.endDate)
        updatedSearchParams.set(searchFields.endDate.name, convertDateToJson(activeFilters.endDate, true) ?? "");
      else updatedSearchParams.delete(searchFields.endDate.name);
      if (activeFilters.assignedTo)
        updatedSearchParams.set(searchFields.assignedTo.name, activeFilters.assignedTo);
      else updatedSearchParams.delete(searchFields.assignedTo.name);
      setSearchParams(updatedSearchParams);
    },
    [filters, searchFields, searchParams, setSearchParams],
  );

  const handleApplyFilters = () => {
    page.current = 1;
    applySearch();
  };

  const handleClearFilters = () => {
    const cleared = { status: [], search: "", startDate: undefined, endDate: undefined, assignedTo: "" };
    setFilters(cleared);
    page.current = 1;
    applySearch(cleared);
  };

  const onPageHandler = useCallback(
    (pageRequest) => {
      page.current = pageRequest + 1;
      applySearch();
    },
    [applySearch],
  );

  const onPageSizeHandler = useCallback(
    (sizeRequest) => {
      limit.current = sizeRequest;
      page.current = 1;
      applySearch();
    },
    [applySearch],
  );

  useEffect(() => {
    applySearch();
  }, []);

  const queryArgs = {
    page: getSearchValue(searchFields.page.name, searchFields.page.default, (x) => parseInt(x)),
    limit: getSearchValue(searchFields.limit.name, searchFields.limit.default, (x) =>
      parseInt(x),
    ),
    status: getSearchValue(searchFields.status.name, ""),  // comma-separated string for API
    search: getSearchValue(searchFields.search.name, ""),
    startDate: getSearchValue(searchFields.startDate.name, ""),
    endDate: getSearchValue(searchFields.endDate.name, ""),
    assignedTo: getSearchValue(searchFields.assignedTo.name, ""),
    ...(sorting.sortBy !== null && { sortBy: sorting.sortBy, sortOrder: sorting.sortOrder }),
  };

  const {
    data: response,
    isLoading,
    isError,
  } = useGetSalesLeadsListQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const { data: usersResponse } = useGetSalesUsersListQuery({
    isActive: true,
  });

  const userOptions = useMemo(() => {
    return (usersResponse?.data || []).map((u) => ({ label: u.name, value: u._id }));
  }, [usersResponse]);

  const allLeads = response?.data || [];

  return (
    <>
      <CommonPage>
        <Content>
          <PageTitle title="Sales Leads">
            <IfHasPermission permissionKey={PermissionKeys.CREATE_LEAD}>
              <InRow gap={2}>
                <PrimaryButton
                  action={() => navigate("/add-sales-leads")}
                  classNames={["text-[16px]"]}>
                  Create Sales Lead
                </PrimaryButton>
                <PrimaryButton
                  action={() => setIsImportDialogOpened(true)}
                  classNames={["text-[16px]"]}>
                  Import Sales Leads
                </PrimaryButton>
              </InRow>
            </IfHasPermission>
          </PageTitle>

          <ResponsiveGap left={false} right={false}>
            <OpenablePanel
              isOpenedByDefault={false}
              openControlRenderer={(action) => (
                <ActionBar classNames={["justify-end"]}>
                  <PrimaryButton action={action}>Advanced Search</PrimaryButton>
                </ActionBar>
              )}
              closeControlRenderer={(action) => (
                <ResponsiveGap top={false} left={false} right={false}>
                  <ActionBar classNames={["justify-end"]}>
                    <InRow>
                      <SecondaryButton action={action}>
                        <GrClose size={20} />
                      </SecondaryButton>
                    </InRow>
                  </ActionBar>
                </ResponsiveGap>
              )}>
              <AdvancedSearchForm
                filters={filters}
                setFilters={setFilters}
                userOptions={userOptions}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </OpenablePanel>
          </ResponsiveGap>

          <LoadableContent isLoading={isLoading} isError={isError}>
            <WithPagination
              id="pagedData"
              currentPage={response?.currentPage - 1 ?? 0}
              totalPages={response?.totalPages ?? 0}
              totalItems={response?.total ?? 0}
              pageSize={limit.current ?? 0}
              onPageChange={onPageHandler}
              onPageSizeChange={onPageSizeHandler}
              paginationWrapper={(x) => <ResponsiveGap>{x}</ResponsiveGap>}>
              <TableView salesLeadsList={allLeads} sorting={sorting} onSortChange={onSortChange} />
            </WithPagination>
          </LoadableContent>
        </Content>
      </CommonPage>
      <ImportSalesLead
        isOpened={isImportDialogOpened}
        onCancel={() => setIsImportDialogOpened(false)}
        onImport={handleImportSalesLead}
        isLoading={isImporting}
      />
    </>
  );
};
